import { api, ApiResponse } from './api';
import { CreatePractitionerRequest } from './types';
import { Practitioner ,PaginationParams, Patient} from '@/lib/types';

export const practitionerService = {
  getPractitionerPatients: (practitionerId: string, params: PaginationParams & { clinic_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.clinic_id) queryParams.append('clinic_id', params.clinic_id);
    return api.get<ApiResponse<{ patients: Patient[], pagination: PaginationParams }>>(`/practitioners/${practitionerId}/patients?${queryParams.toString()}`);
  },
  // Authenticated NPI check (for logged-in users)
  checkPractitionerNpiExists: (npi: string): Promise<{ exists: boolean; practitioner?: { id: string; name: string; npi: string } }> => {
    return api.get<{ data: { exists: boolean; practitioner?: { id: string; name: string; npi: string } } }>(`clinic/practitioners/check-npi/${npi}`).then(res => res.data);
  },
  // Public NPI check (for onboarding flow - no auth required)
  checkPractitionerNpiExistsPublic: (npi: string): Promise<{ exists: boolean; practitioner?: { id: string; name: string; npi: string; email?: string } }> => {
    return api.get<{ data: { exists: boolean; practitioner?: { id: string; name: string; npi: string; email?: string } } }>(`public/practitioners/check-npi/${npi}`).then(res => res.data);
  },
  // Authenticated email check (for logged-in users)
  checkPractitionerEmailExists: (email: string): Promise<{ exists: boolean; practitioner?: { id: string; name: string; npi: string; email: string } }> => {
    return api.get<{ data: { exists: boolean; practitioner?: { id: string; name: string; npi: string; email: string } } }>(`clinic/practitioners/check-email/${encodeURIComponent(email)}`).then(res => res.data);
  },
  getPractitionerDropdownByClinic: (clinicId: string,params: any) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    return api.get(`clinic/${clinicId}/practitioners-dropdown?${queryParams.toString()}`);
  },
  getPractitionerMedicationTemplate: (practitionerId: string, patientId?: string) => {
    const queryParams = new URLSearchParams();
    if (patientId) queryParams.append('patientId', patientId);
    const query = queryParams.toString();
    return api.get(`clinic/practitioners/${practitionerId}/medication-template${query ? `?${query}` : ''}`)
  },

  getPatientMedicationTemplate: (patientId: string) => {
    return api.get(`clinic/patients/${patientId}/medication-template`);
  },

  createPractitioner: (clinicId: string, practitionerData: CreatePractitionerRequest) => {
    return api.post<ApiResponse<Practitioner>>(`clinic/${clinicId}/practitioners`, practitionerData);
  },

  linkToClinic: (clinicId: string, practitionerId: string) => {
    return api.post(`clinic/${clinicId}/practitioners/${practitionerId}/link`);
  },

  getPractitionerByClinicId: (clinicId: string, params: any) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    return api.get(`clinic/${clinicId}/practitioners?${queryParams.toString()}`).then(res => res.data);
  },

  getPractitionerById: (clinicId: string, practitionerId: string) => {
    return api.get<ApiResponse<{ practitioner: Practitioner }>>(`clinic/${clinicId}/practitioners/${practitionerId}`);
  },

  archivePractitioner: (clinicId: string, practitionerId: string) => {
    return api.patch(`clinic/${clinicId}/practitioners/${practitionerId}/deactivate`);
  },

  restorePractitioner: (clinicId: string, practitionerId: string) => {
    return api.patch(`clinic/${clinicId}/practitioners/${practitionerId}/reactivate`);
  },

  sendInvite: (
    clinicId: string,
    email: string,
    firstName?: string,
    lastName?: string,
  ) => {
    return api.post(`clinic/${clinicId}/practitioners/invite`, {
      email,
      first_name: firstName?.trim() || undefined,
      last_name: lastName?.trim() || undefined,
    });
  },

  validateOnboardingInvite: (token: string) => {
    return api.post('clinic/practitioners/validate-invite', { token });
  },

  submitOnboarding: (
    token: string,
    practitionerData: CreatePractitionerRequest & { link_existing_practitioner_id?: string }
  ) => {
    return api.post('clinic/practitioners/onboarding', { token, ...practitionerData });
  },

  approvePractitioner: (clinicId: string, practitionerId: string) => {
    return api.post(`clinic/${clinicId}/practitioners/${practitionerId}/approve`);
  },

  getInvitations: (clinicId: string, params: any) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    return api.get(`clinic/${clinicId}/practitioners/invitations?${queryParams.toString()}`).then(res => res.data);
  },

  resendInvitation: (clinicId: string, invitationId: string) => {
    return api.post(`clinic/${clinicId}/practitioners/invitations/${invitationId}/resend`);
  },

  cancelInvitation: (clinicId: string, invitationId: string) => {
    return api.delete(`clinic/${clinicId}/practitioners/invitations/${invitationId}`);
  },

  resubmitPractitioner: (clinicId: string, practitionerId: string, practitionerData?: any) => {
    return api.patch(`clinic/${clinicId}/practitioners/${practitionerId}/resubmit`, practitionerData);
  },

  updatePractitioner: (practitionerId: string, practitionerData: any) => {
    return api.put(`super-admin/practitioners/${practitionerId}`, practitionerData);
  },
};
